import { useEffect } from "react";

export interface CourseModuleProps {
  name: string;
  description: string;
  position: number;
  duration?: string;
  url?: string;
}

export interface CourseSchemaProps {
  name: string;
  description: string;
  provider: string;
  providerUrl?: string;
  url: string;
  image?: string;
  courseMode?: "online" | "onsite" | "blended";
  educationalLevel?: "beginner" | "intermediate" | "advanced";
  inLanguage?: string;
  isAccessibleForFree?: boolean;
  hasCertification?: boolean;
  totalDuration?: string;
  numberOfModules?: number;
  modules?: CourseModuleProps[];
  skills?: string[];
  dateCreated?: string;
  dateModified?: string;
  rating?: {
    value: number;
    count: number;
  };
}

/**
 * CourseSchema component for educational content SEO
 * Generates Schema.org Course markup for Google Search
 */
export function CourseSchema({
  name,
  description,
  provider,
  providerUrl = "https://datahireclub.com.br",
  url,
  image,
  courseMode = "online",
  educationalLevel = "beginner",
  inLanguage = "pt-BR",
  isAccessibleForFree = false,
  hasCertification = true,
  totalDuration,
  numberOfModules,
  modules = [],
  skills = [],
  dateCreated,
  dateModified,
  rating
}: CourseSchemaProps) {
  useEffect(() => {
    const schemaId = `course-schema-${name.replace(/\s+/g, '-').toLowerCase()}`;
    
    // Remove existing schema
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": name,
      "description": description,
      "provider": {
        "@type": "Organization",
        "name": provider,
        "sameAs": providerUrl
      },
      "url": url,
      "inLanguage": inLanguage,
      "isAccessibleForFree": isAccessibleForFree,
      "courseMode": courseMode,
      "educationalLevel": educationalLevel,
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": courseMode,
        "courseWorkload": totalDuration || "PT10H"
      }
    };

    if (image) {
      schema.image = image;
    }

    if (hasCertification) {
      schema.educationalCredentialAwarded = {
        "@type": "EducationalOccupationalCredential",
        "name": `Certificado ${name}`,
        "credentialCategory": "Certificate"
      };
    }

    if (skills.length > 0) {
      schema.teaches = skills.map(skill => ({
        "@type": "DefinedTerm",
        "name": skill
      }));
      schema.about = skills;
    }

    if (modules.length > 0) {
      schema.hasPart = modules.map((module, index) => ({
        "@type": "Course",
        "name": module.name,
        "description": module.description,
        "position": module.position || index + 1,
        "url": module.url,
        "timeRequired": module.duration
      }));
    }

    if (numberOfModules) {
      schema.numberOfCredits = numberOfModules;
    }

    if (dateCreated) {
      schema.dateCreated = dateCreated;
    }

    if (dateModified) {
      schema.dateModified = dateModified;
    }

    if (rating) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": rating.value,
        "reviewCount": rating.count,
        "bestRating": 5,
        "worstRating": 1
      };
    }

    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(schemaId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, provider, providerUrl, url, image, courseMode, educationalLevel, 
      inLanguage, isAccessibleForFree, hasCertification, totalDuration, numberOfModules, 
      modules, skills, dateCreated, dateModified, rating]);

  return null;
}

export default CourseSchema;
